const GAIJIN_SSO_BASE_URL = 'https://login.gaijin.net';
const OAUTH_CLIENT_ID = process.env.REACT_APP_GAIJIN_CLIENT_ID || 'YOUR_CLIENT_ID';
const OAUTH_REDIRECT_URI = process.env.REACT_APP_REDIRECT_URI || window.location.origin + '/callback';
const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT || '';

const SSO_JWT_KEY = 'gaijin_sso_jwt';
const REFRESH_TOKEN_KEY = 'gaijin_refresh_token';
const USER_INFO_KEY = 'gaijin_user_info';
const TOKEN_EXPIRY_KEY = 'gaijin_token_expiry';
const SESSION_START_KEY = 'gaijin_session_start';
const LAST_ACTIVITY_KEY = 'gaijin_last_activity';

const MAX_SESSION_DURATION_MS = 24 * 60 * 60 * 1000;
const IDLE_TIMEOUT_MS = 2 * 60 * 60 * 1000;

export interface GaijinUser {
    uid: string;
    nick?: string;
    email?: string;
    tags?: string;
    lng?: string;
    cntry?: string;
}

export class GaijinAuthService {
    static isAuthenticated(): boolean {
        const token = this.getAccessToken();
        if (!token) return false;

        const now = Date.now();

        const expiry = localStorage.getItem(TOKEN_EXPIRY_KEY);
        if (expiry && now >= parseInt(expiry)) {
            this.clearAuth();
            return false;
        }

        const sessionStart = localStorage.getItem(SESSION_START_KEY);
        if (sessionStart && now - parseInt(sessionStart) >= MAX_SESSION_DURATION_MS) {
            this.clearAuth();
            return false;
        }

        const lastActivity = localStorage.getItem(LAST_ACTIVITY_KEY);
        if (lastActivity && now - parseInt(lastActivity) >= IDLE_TIMEOUT_MS) {
            this.clearAuth();
            return false;
        }

        return true;
    }

    private static lastTouchTime = 0;

    static touchActivity() {
        const now = Date.now();
        if (now - this.lastTouchTime < 1000) return;
        this.lastTouchTime = now;
        localStorage.setItem(LAST_ACTIVITY_KEY, now.toString());
    }

    static getAccessToken(): string | null {
        return localStorage.getItem(SSO_JWT_KEY);
    }

    static getRefreshToken(): string | null {
        return localStorage.getItem(REFRESH_TOKEN_KEY);
    }

    static getCurrentUser(): GaijinUser | null {
        const userJson = localStorage.getItem(USER_INFO_KEY);
        if (!userJson) return null;

        try {
            return this.normalizeUserInfo(JSON.parse(userJson));
        } catch {
            return null;
        }
    }

    private static normalizeUserInfo(source: any): GaijinUser | null {
        if (!source || typeof source !== 'object') {
            return null;
        }

        return {
            uid: source.uid || source.sub || 'unknown',
            nick: source.nick,
            email: source.email,
            tags: source.tags,
            lng: source.lng,
            cntry: source.cntry
        };
    }

    private static persistUserInfo(source: any): GaijinUser | null {
        const userInfo = this.normalizeUserInfo(source);
        if (!userInfo) {
            return null;
        }

        localStorage.setItem(USER_INFO_KEY, JSON.stringify(userInfo));
        return userInfo;
    }

    static async initiateLogin() {
        const state = this.generateRandomString(32);
        sessionStorage.setItem('oauth_state', state);

        // Preserve tester-link prefill params (?sg=&app=&region=) across the SSO redirect;
        // LoginPage restores them onto the URL after the callback completes. Skip OAuth
        // callback params (?code=&state=) so retrying a failed callback can't clobber the
        // original tester-link params.
        const preLoginQuery = window.location.search;
        if (preLoginQuery && !new URLSearchParams(preLoginQuery).has('code')) {
            sessionStorage.setItem('post_login_query', preLoginQuery);
        } else {
            // Nothing to preserve on this login — drop any stale params left by an earlier
            // failed tester-link login so they aren't restored onto this unrelated session.
            sessionStorage.removeItem('post_login_query');
        }

        const params = new URLSearchParams({
            client_id: OAUTH_CLIENT_ID,
            redirect_uri: OAUTH_REDIRECT_URI,
            response_type: 'code',
            response_mode: 'query',
            state: state,
            scope: 'openid profile email'
        });

        window.location.href = `${GAIJIN_SSO_BASE_URL}/proxy/oauth/authorize?${params.toString()}`;
    }

    static async handleCallback(): Promise<boolean> {
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        const state = urlParams.get('state');
        const error = urlParams.get('error');

        if (error) {
            throw new Error(`OAuth error: ${error}`);
        }

        const storedState = sessionStorage.getItem('oauth_state');
        if (!state || state !== storedState) {
            throw new Error('Login failed: Invalid state parameter');
        }

        sessionStorage.removeItem('oauth_state');

        if (!code) {
            throw new Error('No authorization code received');
        }

        const tokenResponse = await fetch(`${API_ENDPOINT}/token-exchange`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                code,
                redirect_uri: OAUTH_REDIRECT_URI,
                client_id: OAUTH_CLIENT_ID
            })
        });

        if (tokenResponse.status === 403) {
            // Server-side access gate denied this user; no token material is returned.
            this.clearAuth();
            window.location.href = '/?error=insufficient_permissions';
            return false;
        }

        if (!tokenResponse.ok) {
            throw new Error('Login failed: Could not exchange authorization code');
        }

        const tokens = await tokenResponse.json();

        if (!tokens.user_permissions?.allowed) {
            this.clearAuth();
            window.location.href = '/?error=insufficient_permissions';
            return false;
        }

        await this.storeTokens(tokens);

        const userInfo = this.getCurrentUser();
        const ssoJwt = tokens.sso_jwt || tokens.access_token;
        if (!userInfo && ssoJwt) {
            this.getUserInfoFromJWT(ssoJwt);
        }

        return true;
    }

    static async loginWithJWT(jwtToken: string): Promise<boolean> {
        const payload = this.parseJWT(jwtToken);
        const userInfo = this.persistUserInfo(payload);

        if (!userInfo) {
            throw new Error('Invalid token or could not extract user info');
        }

        localStorage.setItem(SSO_JWT_KEY, jwtToken);

        if (payload && payload.exp) {
            localStorage.setItem(TOKEN_EXPIRY_KEY, (payload.exp * 1000).toString());
        }

        const now = Date.now().toString();
        localStorage.setItem(SESSION_START_KEY, now);
        localStorage.setItem(LAST_ACTIVITY_KEY, now);

        return true;
    }

    private static async storeTokens(tokens: any) {
        const ssoJwt = tokens.sso_jwt || tokens.access_token;
        if (ssoJwt) {
            localStorage.setItem(SSO_JWT_KEY, ssoJwt);

            const accessPayload = this.parseJWT(ssoJwt);
            if (accessPayload && accessPayload.exp) {
                localStorage.setItem(TOKEN_EXPIRY_KEY, (accessPayload.exp * 1000).toString());
            } else if (tokens.expires_in) {
                const expiryTime = Date.now() + (tokens.expires_in * 1000);
                localStorage.setItem(TOKEN_EXPIRY_KEY, expiryTime.toString());
            }

            if (accessPayload) {
                this.persistUserInfo(accessPayload);
            }
        }

        const now = Date.now().toString();
        localStorage.setItem(SESSION_START_KEY, now);
        localStorage.setItem(LAST_ACTIVITY_KEY, now);

        if (tokens.refresh_token) {
            localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refresh_token);
        }

        if (tokens.id_token && !localStorage.getItem(USER_INFO_KEY)) {
            try {
                const idPayload = this.parseJWT(tokens.id_token);
                if (idPayload) {
                    this.persistUserInfo(idPayload);
                }
            } catch {
                // id_token not parseable
            }
        }
    }

    private static getUserInfoFromJWT(token: string): GaijinUser | null {
        const payload = this.parseJWT(token);
        if (!payload) {
            return this.getCurrentUser();
        }
        return this.persistUserInfo(payload) || this.getCurrentUser();
    }

    private static parseJWT(token: string): any {
        try {
            const parts = token.split('.');
            if (parts.length !== 3) return null;
            const payload = parts[1];
            const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
            return JSON.parse(decoded);
        } catch {
            return null;
        }
    }

    static async refreshAccessToken(): Promise<boolean> {
        const refreshToken = this.getRefreshToken();
        if (!refreshToken) return false;

        try {
            const response = await fetch(`${GAIJIN_SSO_BASE_URL}/proxy/oauth/token`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    grant_type: 'refresh_token',
                    refresh_token: refreshToken,
                    client_id: OAUTH_CLIENT_ID
                })
            });

            if (!response.ok) {
                this.clearAuth();
                return false;
            }

            const tokens = await response.json();
            await this.storeTokens(tokens);
            return true;
        } catch {
            this.clearAuth();
            return false;
        }
    }

    static signOut() {
        this.clearAuth();
    }

    private static clearAuth() {
        localStorage.removeItem(SSO_JWT_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        localStorage.removeItem(USER_INFO_KEY);
        localStorage.removeItem(TOKEN_EXPIRY_KEY);
        localStorage.removeItem(SESSION_START_KEY);
        localStorage.removeItem(LAST_ACTIVITY_KEY);
    }

    private static generateRandomString(length: number): string {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        let result = '';
        const randomValues = new Uint8Array(length);
        crypto.getRandomValues(randomValues);

        for (let i = 0; i < length; i++) {
            result += chars[randomValues[i] % chars.length];
        }

        return result;
    }
}
